import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUnit } from "effector-react";
import { ChatInput } from "../ChatInput";
import { $sightsStore, $sceneSights, $sceneOrderedSightIds, $suppressedSightIds, $currentSight, userSelectedSight, sceneOrbitInteractionStarted } from "../../state/appState";
import { enterInsideScene } from "../../state/insideSceneEntry";
import { $nextSceneIdForCurrentScene, $tourEntriesForCurrentScene } from "../../state/visitedSights";
import { PoiChips, type NextSceneSnapshot } from "../PoiChips";
import { POI_CHIPS_HEIGHT } from "../../context/CameraViewOffsetContext";
import type { AssistantSuggestion } from "../../types/assistant";
import type { Photo, Video, Sight, SightId } from "../../types";
import { useAudioController } from "../../hooks/useAudioController";
import { useLayoutMode } from "../../hooks/useLayoutMode";
import {
  AssistantChatPanel,
  type AssistantChatMode,
  type AssistantChatPanelHandle,
} from "../AssistantChatPanel";
import { AssistantChatSidebar } from "../AssistantChatSidebar";
import { useOverscrollDrag } from "../../hooks/useOverscrollDrag";
import { Suggestions } from "../Suggestions";
import { PinnedUserMessage } from "../PinnedUserMessage";
import { ScrollToBottomButton } from "../ScrollToBottomButton";
import cx from "classnames";
import { LoadingText } from "../LoadingText";
import { AssistantMessage } from "../AssistantMessage";
import { PhotoViewer } from "../PhotoViewer";
import {
  $assistantIsBusy,
  $assistantMessages,
  $assistantStatus,
  $assistantThinkingText,
  $assistantViewerIndex,
  $assistantViewerItems,
  $assistantViewerOpen,
  $assistantWaitingSceneOpen,
  assistantPromptSubmitted,
  assistantStopRequested,
  assistantSuggestionSelected,
  assistantTextAnimationCompleted,
  assistantViewerClosed,
  assistantViewerIndexChanged,
  assistantViewerOpened,
  assistantPoiClickRequested,
} from "../../state/assistantHud";
import { $devConfig } from "../../state/appConfig";
import { $activeScene, $activeSceneId, $isPoiArtifactOpen, openPoiArtifact, enterScene, resetNavigation } from "../../state/sceneNavigation";
import { useI18n } from "../../i18n";
import { getPoiArtifactIds } from "../../scene/poiArtifacts/registry";
import { isInsideScene } from "../../scene/insideScenes/registry";
import { isEraScene } from "../../scene/eraScenes/registry";
import { resolveMainBannerSceneUrlId, resolveNextSceneTarget } from "../../scene/sceneBannerMap";
import { SCENE_POI_ID } from "../../scene/sceneNavigationMap";
import { getSplatSceneConfig } from "../../scene/splatRegistry";
import { $historyMode, navigateToHistoricalScene } from "../../state/historicalScenes";
import { sceneEntered } from "../../analytics/sceneTracker";
import {
  sceneBannerClicked,
  nextSceneChipClicked,
} from "../../analytics/navigationTracker";
import { chatPanelModeChanged } from "../../analytics/chatStateTracker";

import styles from "./AssistantHud.module.css";

const PINNED_BAR_HEIGHT = 40;

type AssistantHudProps = {
  animateInitialOffset?: boolean;
  /** When true, the entire HUD is hidden via display:none (preserves DOM & scroll). */
  hidden?: boolean;
};

export function AssistantHud({ animateInitialOffset, hidden }: AssistantHudProps = {}) {
  const {
    sights,
    sceneSights,
    orderedSightIds,
    currentSight,
    messages,
    status,
    isBusy,
    thinkingText,
    viewerItems,
    viewerOpen,
    viewerIndex,
    devConfig,
    activeSceneId,
    activeScene,
    isPoiArtifactOpen,
    tourEntries,
    nextSceneId,
    waitingSceneOpen,
    suppressedSightIds,
  } = useUnit({
    sights: $sightsStore,
    sceneSights: $sceneSights,
    orderedSightIds: $sceneOrderedSightIds,
    suppressedSightIds: $suppressedSightIds,
    currentSight: $currentSight,
    messages: $assistantMessages,
    status: $assistantStatus,
    isBusy: $assistantIsBusy,
    thinkingText: $assistantThinkingText,
    viewerItems: $assistantViewerItems,
    viewerOpen: $assistantViewerOpen,
    viewerIndex: $assistantViewerIndex,
    devConfig: $devConfig,
    activeSceneId: $activeSceneId,
    activeScene: $activeScene,
    isPoiArtifactOpen: $isPoiArtifactOpen,
    tourEntries: $tourEntriesForCurrentScene,
    nextSceneId: $nextSceneIdForCurrentScene,
    waitingSceneOpen: $assistantWaitingSceneOpen,
  });

  const i18n = useI18n();

  // [TEMPORARY] Mock PoiArtifact suggestion buttons
  const mockPoiArtifactSuggestions: AssistantSuggestion[] = useMemo(() => {
    if (!devConfig.mockPoiArtifactButtons) return [];
    return getPoiArtifactIds().map((id) => ({
      type: 'enter-poi-artifact' as const,
      sceneId: id,
      label: id,
    }));
  }, [devConfig.mockPoiArtifactButtons]);

  const [
    submitPrompt,
    selectSuggestion,
    completeTextAnimation,
    openViewer,
    closeViewer,
    setViewerIndex,
    requestStop,
  ] = useUnit([
    assistantPromptSubmitted,
    assistantSuggestionSelected,
    assistantTextAnimationCompleted,
    assistantViewerOpened,
    assistantViewerClosed,
    assistantViewerIndexChanged,
    assistantStopRequested,
  ]);
  const [navigateToSight, poiClickRequested, triggerOpenPoiArtifact] = useUnit([userSelectedSight, assistantPoiClickRequested, openPoiArtifact]);
  useAudioController();
  // Reactive layout switch — desktop renders `AssistantChatSidebar`
  // (fixed-width right panel, always visible), mobile keeps the
  // existing `AssistantChatPanel` (drag-modes + keyboard hacks).
  // Cross-breakpoint resize swaps the wrapper without remounting
  // the Effector store / Chat messages.
  const layoutMode = useLayoutMode();
  // Desktop semantics: sidebar is always visible — initial chatMode
  // "half-screen" makes every `chatMode === "hidden"` predicate (which
  // hide scroll-to-bottom button, pinned bar, etc.) evaluate as
  // "open", so the desktop UI behaves like an active mobile chat from
  // mount. The state stays mostly dormant on desktop; the
  // `AssistantChatPanel` is the only consumer that mutates it.
  const [chatMode, setChatMode] = useState<AssistantChatMode>(() =>
    layoutMode === "desktop" ? "half-screen" : "hidden"
  );
  // Cross-breakpoint guard: mobile→desktop resize keeps the existing
  // chatMode (could be "hidden" if user never opened the panel) —
  // force to "half-screen" so desktop predicates behave correctly.
  // Inverse (desktop→mobile) leaves chatMode = "half-screen" which
  // mounts the mobile panel in half-screen instead of hidden — also
  // acceptable, since the user has just engaged with the desktop chat.
  useEffect(() => {
    if (layoutMode === "desktop" && chatMode === "hidden") {
      setChatMode("half-screen");
    }
  }, [layoutMode, chatMode]);
  const [requestedMode, setRequestedMode] = useState<AssistantChatMode | null>(
    null
  );
  const requestedModeRef = useRef(requestedMode);
  useEffect(() => {
    requestedModeRef.current = requestedMode;
  }, [requestedMode]);
  const [isSliding, setIsSliding] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const historyRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<AssistantChatPanelHandle | null>(null);
  const userScrolledRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const historyInRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(0);
  const lastNonFullscreenModeRef = useRef<AssistantChatMode | null>(null);
  const dismissKeyboardTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const keyboardDismissGestureActiveRef = useRef(false);
  const lastAnimatedMessageIdRef = useRef<string | null>(null);
  const userScrollRetryTimeoutRef = useRef<number | null>(null);
  const stopScrollRequestedRef = useRef(false);
  const pendingUserScrollRef = useRef(false);
  const pendingScrollTimeoutRef = useRef<number | null>(null);
  const overflowCheckRafRef = useRef<number>(0);
  const initialScrollDoneRef = useRef(false);
  const chatModeRef = useRef(chatMode);
  const layoutModeRef = useRef(layoutMode);
  const [isAnimatingMessage, setIsAnimatingMessage] = useState(false);
  const [pinnedMessage, setPinnedMessage] = useState<{ id: string; text: string } | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // POI chips are fully tour-driven: only sights that the backend includes
  // in `tour` for the current scene are rendered. Empty tour → empty chip
  // strip. `sceneSights` (from `/api/config.pois`) is consulted only to
  // look up each sight's display data (name, position, etc.) — never as a
  // fallback or supplement to the tour list.
  const poiLocations = useMemo(() => {
    if (tourEntries.length === 0) {
      return { unvisited: [] as Sight[], visited: [] as Sight[] };
    }

    const sightById = new Map<SightId, Sight>();
    for (const id of orderedSightIds) {
      const s = sceneSights[id];
      if (s) sightById.set(id, s);
    }

    const unvisited: Sight[] = [];
    const visited: Sight[] = [];
    for (const entry of tourEntries) {
      const sight = sightById.get(entry.sightId);
      if (!sight) continue; // entry references a sight not present in this scene's sceneSights — skip
      // Withheld while its subject is elsewhere — the crow finishing a skipped
      // tour. Same set the marker layer is given, so the chip and the 3D marker
      // come and go together.
      if (suppressedSightIds.has(entry.sightId)) continue;
      if (entry.visited) visited.push(sight);
      else unvisited.push(sight);
    }

    return { unvisited, visited };
  }, [orderedSightIds, sceneSights, tourEntries, suppressedSightIds]);

  const handlePoiSelect = useCallback(
    (id: SightId) => {
      navigateToSight({ id, smooth: true, source: 'poi' });
      poiClickRequested({ id });
    },
    [navigateToSight, poiClickRequested],
  );

  const disablePoiChips = useMemo(() => {
    if (!activeSceneId) return false;
    return getSplatSceneConfig(activeSceneId)?.disablePoiChips === true;
  }, [activeSceneId]);

  // Snapshot для золотой кнопки "Next scene — {name}". Backend присылает
  // её через tour-item `{ kind: 'scene' }` когда все POI текущей сцены
  // visited. Если id не распознан (`resolveNextSceneTarget` вернул null)
  // или уже на этой сцене (`activeSceneId === target.sceneId`) — кнопка
  // не показывается. Store `$nextSceneIdForCurrentScene` уже фильтрует
  // случай "echo текущей сцены", но дополнительная защита по URL-id
  // не помешает (бек мог прислать backend-id который разрешается в
  // текущую URL-сцену).
  const nextSceneSnapshot = useMemo<NextSceneSnapshot | null>(() => {
    if (!nextSceneId || disablePoiChips) return null;
    const target = resolveNextSceneTarget(nextSceneId);
    if (!target) return null;
    const resolvedUrlId =
      resolveMainBannerSceneUrlId(target.sceneId) ?? target.sceneId;
    if (resolvedUrlId === activeSceneId) return null;
    const name = i18n.globalMap.markerLabels[target.labelKey] ?? target.labelKey;
    return {
      sceneId: target.sceneId,
      label: i18n.assistant.nextScene.replace("{name}", name),
      iconSrc: target.iconSrc,
    };
  }, [nextSceneId, disablePoiChips, activeSceneId, i18n]);

  const hasNextScene = nextSceneSnapshot !== null;
  const totalPoiCount = disablePoiChips
    ? 0
    : poiLocations.unvisited.length + poiLocations.visited.length + (hasNextScene ? 1 : 0);
  const poiChipsHeight = totalPoiCount > 0 ? POI_CHIPS_HEIGHT + 6 : 0;

  useOverscrollDrag({
    scrollRef: historyRef,
    callbacks: {
      onMove: (e) => dragRef.current?.handleDragMove(e),
      onEnd: (e) => dragRef.current?.handleDragEnd(e),
    },
    // Desktop sidebar has no drag-modes — overscroll on the history
    // scroll container should NOT bubble into a panel-mode change.
    enabled:
      layoutMode === "mobile" &&
      (chatMode === "half-screen" || chatMode === "full-screen"),
  });

  useEffect(() => {
    chatModeRef.current = chatMode;
  }, [chatMode]);

  useEffect(() => {
    layoutModeRef.current = layoutMode;
  }, [layoutMode]);

  // Auto-expand the 3D view when the user starts rotating/zooming the scene
  // while the chat is at half-screen (mobile only). Mirrors dragging the panel
  // down. POI taps go through `userSelectedSight` (not the orbit-start hook),
  // so tapping a POI keeps the chat at its current size and just shows the
  // POI's text — exactly the intended split. Subscribed via `.watch` (reading
  // current values through refs) because the event reference is stable and a
  // dep-based effect would never re-fire.
  useEffect(() => {
    const sub = sceneOrbitInteractionStarted.watch(() => {
      if (
        layoutModeRef.current === "mobile" &&
        chatModeRef.current === "half-screen"
      ) {
        setRequestedMode("hidden");
      }
    });
    return () => sub.unsubscribe();
  }, []);

  // Mobile: collapse the chat to `hidden` when the historical-scenes feature is
  // opened (clock tap), so the modal banner/slider isn't crowded by the panel.
  // Fires only on the closed→open transition, and only if the chat is currently
  // open — so it doesn't fight the user if they later drag the panel back up.
  const historyMode = useUnit($historyMode);
  const prevHistoryModeRef = useRef(historyMode);
  useEffect(() => {
    const prev = prevHistoryModeRef.current;
    prevHistoryModeRef.current = historyMode;
    if (
      prev === "closed" &&
      historyMode !== "closed" &&
      layoutMode === "mobile" &&
      chatModeRef.current !== "hidden"
    ) {
      setRequestedMode("hidden");
    }
  }, [historyMode, layoutMode]);

  // Аналитика: каждое изменение режима панели = 1 event. Первый emit на
  // mount помечается `previousMode: null` (initial "hidden" из useState).
  const previousChatModeRef = useRef<AssistantChatMode | null>(null);
  useEffect(() => {
    chatPanelModeChanged({
      previousMode: previousChatModeRef.current,
      newMode: chatMode,
    });
    previousChatModeRef.current = chatMode;
  }, [chatMode]);

  const detectPinnedMessage = useCallback(() => {
    const history = historyRef.current;
    if (!history) {
      setPinnedMessage(null);
      return;
    }

    const historyRect = history.getBoundingClientRect();
    const threshold = historyRect.top + 8;
    const userMsgs = history.querySelectorAll<HTMLElement>('[data-role="user"]');
    let found: { id: string; text: string } | null = null;

    for (const el of userMsgs) {
      if (el.getBoundingClientRect().bottom < threshold) {
        found = {
          id: el.dataset.messageId ?? "",
          text: el.textContent?.trim() ?? "",
        };
      } else {
        break;
      }
    }

    setPinnedMessage(found);
  }, []);

  const scrollToLastUserMessage = useCallback(() => {
    const history = historyRef.current;
    const historyIn = historyInRef.current;
    if (!history || !historyIn) return;

    const userMsgs = history.querySelectorAll('[data-role="user"]');
    const lastUserMsg = userMsgs[userMsgs.length - 1] as HTMLElement | undefined;
    if (!lastUserMsg) return;

    historyIn.style.paddingBottom = `${history.clientHeight}px`;

    const historyRect = history.getBoundingClientRect();
    const msgRect = lastUserMsg.getBoundingClientRect();
    const targetScroll = history.scrollTop + (msgRect.bottom - historyRect.top);

    isProgrammaticScrollRef.current = true;
    history.scrollTo({ top: targetScroll, behavior: "smooth" });

    const onScrollEnd = () => {
      isProgrammaticScrollRef.current = false;
      userScrolledRef.current = false;
      history.removeEventListener("scrollend", onScrollEnd);
      const dist = history.scrollHeight - history.scrollTop - history.clientHeight;
      setShowScrollToBottom(dist > 100);
      detectPinnedMessage();
    };
    history.addEventListener("scrollend", onScrollEnd, { once: true });

    setTimeout(() => {
      if (isProgrammaticScrollRef.current) {
        onScrollEnd();
      }
    }, 800);
  }, [detectPinnedMessage]);

  const checkContentOverflow = useCallback(() => {
    if (isProgrammaticScrollRef.current) return;
    if (overflowCheckRafRef.current !== 0) return;

    overflowCheckRafRef.current = requestAnimationFrame(() => {
      overflowCheckRafRef.current = 0;
      const history = historyRef.current;
      const historyIn = historyInRef.current;
      if (!history || !historyIn) return;

      const assistantMsgs = historyIn.querySelectorAll('[data-role="assistant"]');
      const lastAssistant = assistantMsgs[assistantMsgs.length - 1] as HTMLElement | undefined;
      if (!lastAssistant) return;

      const lastRect = lastAssistant.getBoundingClientRect();
      const historyRect = history.getBoundingClientRect();
      const overflow = lastRect.bottom - historyRect.bottom;

      setShowScrollToBottom(overflow > 20);
    });
  }, []);

  const handleHistoryScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) return;
    const history = historyRef.current;
    if (!history) return;

    const historyIn = historyInRef.current;
    let distanceToBottom: number;

    if (isAnimatingMessage && historyIn) {
      const assistantMsgs = historyIn.querySelectorAll('[data-role="assistant"]');
      const lastAssistant = assistantMsgs[assistantMsgs.length - 1] as HTMLElement | undefined;
      if (lastAssistant) {
        const lastRect = lastAssistant.getBoundingClientRect();
        const historyRect = history.getBoundingClientRect();
        distanceToBottom = lastRect.bottom - historyRect.bottom;
      } else {
        distanceToBottom = 0;
      }
    } else {
      distanceToBottom =
        history.scrollHeight - history.scrollTop - history.clientHeight;
    }

    const threshold = 8;
    userScrolledRef.current = distanceToBottom > threshold;
    setShowScrollToBottom(distanceToBottom > 100);
    detectPinnedMessage();
  }, [detectPinnedMessage, isAnimatingMessage]);

  const scrollToBottom = useCallback(() => {
    const history = historyRef.current;
    const historyIn = historyInRef.current;
    if (!history) return;
    isProgrammaticScrollRef.current = true;
    setShowScrollToBottom(false);

    let targetTop = history.scrollHeight;
    if (isAnimatingMessage && historyIn) {
      const assistantMsgs = historyIn.querySelectorAll('[data-role="assistant"]');
      const lastAssistant = assistantMsgs[assistantMsgs.length - 1] as HTMLElement | undefined;
      if (lastAssistant) {
        const lastRect = lastAssistant.getBoundingClientRect();
        const historyRect = history.getBoundingClientRect();
        targetTop = history.scrollTop + (lastRect.bottom - historyRect.bottom);
      }
    }
    history.scrollTo({ top: targetTop, behavior: "smooth" });

    const onEnd = () => {
      isProgrammaticScrollRef.current = false;
      userScrolledRef.current = false;
      history.removeEventListener("scrollend", onEnd);
      detectPinnedMessage();
    };
    history.addEventListener("scrollend", onEnd, { once: true });
    setTimeout(() => {
      if (isProgrammaticScrollRef.current) onEnd();
    }, 800);
  }, [detectPinnedMessage, isAnimatingMessage]);

  const handleTextAnimationComplete = useCallback(
    (messageId: string) => {
      completeTextAnimation(messageId);
      if (overflowCheckRafRef.current) {
        cancelAnimationFrame(overflowCheckRafRef.current);
        overflowCheckRafRef.current = 0;
      }
      if (lastAnimatedMessageIdRef.current === messageId) {
        setIsAnimatingMessage(false);
      }
      if (!stopScrollRequestedRef.current && historyInRef.current) {
        requestAnimationFrame(() => {
          if (historyInRef.current) {
            historyInRef.current.style.paddingBottom = "";
          }
          requestAnimationFrame(() => {
            const history = historyRef.current;
            if (!history) return;
            const dist =
              history.scrollHeight - history.scrollTop - history.clientHeight;
            setShowScrollToBottom(dist > 100);
          });
        });
      }
    },
    [completeTextAnimation]
  );

  useEffect(() => {
    if (!isAnimatingMessage && stopScrollRequestedRef.current) {
      stopScrollRequestedRef.current = false;
      requestAnimationFrame(() => {
        if (historyInRef.current) {
          historyInRef.current.style.paddingBottom = "";
        }
        const history = historyRef.current;
        if (history) {
          history.scrollTop = history.scrollHeight;
          userScrolledRef.current = false;
          isProgrammaticScrollRef.current = false;
          setShowScrollToBottom(false);
          detectPinnedMessage();
        }
      });
    }
  }, [isAnimatingMessage, detectPinnedMessage]);

  const handleStop = useCallback(() => {
    stopScrollRequestedRef.current = true;
    requestStop();
  }, [requestStop]);

  const handleChatSubmit = useCallback(
    (message: string) => {
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;

      if (chatMode === "hidden") {
        setRequestedMode("half-screen");
      } else if (isTouchDevice) {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }

        if (lastNonFullscreenModeRef.current !== null) {
          // Clear saved mode so the blur effect (isInputFocused → false) doesn't restore it.
          // Panel stays in full-screen; scenePeek and camera offset adjust reactively
          // as the keyboard closes and viewport metrics stabilize.
          lastNonFullscreenModeRef.current = null;
        }
      }

      submitPrompt(message);
    },
    [chatMode, submitPrompt]
  );

  const handlePanelGestureChange = useCallback((sliding: boolean) => {
    if (!sliding) {
      keyboardDismissGestureActiveRef.current = false;
    }
    setIsSliding(sliding);
  }, []);

  const handleKeyboardDismissGestureStart = useCallback(() => {
    keyboardDismissGestureActiveRef.current = true;
  }, []);

  const handleInputFocus = useCallback(() => {
    clearTimeout(dismissKeyboardTimerRef.current);
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
  }, []);

  useEffect(() => {
    return () => clearTimeout(dismissKeyboardTimerRef.current);
  }, []);

  const handleModeChange = useCallback(
    (mode: AssistantChatMode) => {
      setChatMode(mode);
      if (mode === "hidden") {
        setShowScrollToBottom(false);
      }
      if (requestedModeRef.current && requestedModeRef.current === mode) {
        setRequestedMode(null);
      }
    },
    []
  );

  const handlePhotosClick = useCallback(
    (items: Array<Photo | Video>) => {
      if (!items.length) {
        return;
      }
      openViewer({ items, index: 0 });
    },
    [openViewer]
  );

  const handleEnterScene = useCallback(
    (sceneId: string) => {
      if (isInsideScene(sceneId)) {
        // Snapshot/save + push-on-stack lives in a shared helper so 3D POI
        // markers with `moveToSceneId` go through the same path
        // (the inside-POI bridge in createSdkScene.ts).
        enterInsideScene(sceneId);
        return;
      }
      if (isEraScene(sceneId)) {
        // Эпоха из next-scene chip (kaaba-2000bce → kaaba-600ce): тот же
        // вход, что у слайдера и меню — push / replace / goBack к «сегодня».
        navigateToHistoricalScene(sceneId);
        return;
      }
      const mainSceneUrlId = resolveMainBannerSceneUrlId(sceneId);
      if (mainSceneUrlId) {
        // Backend для основных сцен присылает poi-filter id (`main` /
        // `arafat-scene` / `mina-scene` / `muzdalifa-scene`); разворачиваем
        // его в URL-id и идём по тому же flow, что в MenuOverlay:
        // resetNavigation сбрасывает стек, enterScene открывает сцену
        // с её собственным poiFilterId.
        if (activeSceneId === mainSceneUrlId) return;
        resetNavigation();
        enterScene({
          sceneId: mainSceneUrlId,
          poiFilterId: SCENE_POI_ID[mainSceneUrlId] ?? mainSceneUrlId,
        });
        sceneEntered(mainSceneUrlId);
        return;
      }
      triggerOpenPoiArtifact({ sceneId });
    },
    [triggerOpenPoiArtifact, activeSceneId],
  );

  // Variant с явным источником клика для аналитики. Чтобы не размазывать
  // attribution по handleEnterScene (он вызывается и из next-scene chip),
  // оборачиваем под конкретный surface.
  const handleBannerEnterScene = useCallback(
    (sceneId: string) => {
      if (isInsideScene(sceneId)) {
        sceneBannerClicked({
          surface: 'chat',
          bannerKind: 'inside',
          targetSceneId: sceneId,
        });
      } else if (resolveMainBannerSceneUrlId(sceneId)) {
        sceneBannerClicked({
          surface: 'chat',
          bannerKind: 'main',
          targetSceneId: sceneId,
        });
      }
      // artifact-кейс трекается через openPoiArtifact в navigationTracker
      handleEnterScene(sceneId);
    },
    [handleEnterScene],
  );

  const handleNextSceneChipSelect = useCallback(
    (sceneId: string) => {
      nextSceneChipClicked({ targetSceneId: sceneId });
      handleEnterScene(sceneId);
    },
    [handleEnterScene],
  );

  const handlePinnedClick = useCallback(() => {
    if (!pinnedMessage) return;
    const history = historyRef.current;
    if (!history) return;

    const target = history.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(pinnedMessage.id)}"]`
    );
    if (!target) return;

    const historyRect = history.getBoundingClientRect();
    const msgRect = target.getBoundingClientRect();
    const targetScroll = history.scrollTop + (msgRect.top - historyRect.top) - 8;

    isProgrammaticScrollRef.current = true;
    history.scrollTo({ top: Math.max(0, targetScroll), behavior: "smooth" });

    const onScrollEnd = () => {
      isProgrammaticScrollRef.current = false;
      history.removeEventListener("scrollend", onScrollEnd);
      detectPinnedMessage();
    };
    history.addEventListener("scrollend", onScrollEnd, { once: true });
    setTimeout(() => {
      if (isProgrammaticScrollRef.current) {
        onScrollEnd();
      }
    }, 800);
  }, [pinnedMessage, detectPinnedMessage]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== "user") return;

    userScrolledRef.current = false;

    if (chatModeRef.current === "hidden") {
      pendingUserScrollRef.current = true;
      return;
    }

    scrollToLastUserMessage();

    if (userScrollRetryTimeoutRef.current !== null) {
      window.clearTimeout(userScrollRetryTimeoutRef.current);
    }
    userScrollRetryTimeoutRef.current = window.setTimeout(() => {
      scrollToLastUserMessage();
      userScrollRetryTimeoutRef.current = null;
    }, 32);
  }, [messages, scrollToLastUserMessage]);

  useEffect(() => {
    if (!pendingUserScrollRef.current || chatMode === "hidden") return;

    pendingUserScrollRef.current = false;

    pendingScrollTimeoutRef.current = window.setTimeout(() => {
      pendingScrollTimeoutRef.current = null;
      if (chatModeRef.current === "hidden") return;
      userScrolledRef.current = false;
      scrollToLastUserMessage();
      userScrollRetryTimeoutRef.current = window.setTimeout(() => {
        scrollToLastUserMessage();
        userScrollRetryTimeoutRef.current = null;
      }, 32);
    }, 350);
  }, [chatMode, scrollToLastUserMessage]);

  useEffect(() => {
    return () => {
      if (userScrollRetryTimeoutRef.current !== null) {
        window.clearTimeout(userScrollRetryTimeoutRef.current);
        userScrollRetryTimeoutRef.current = null;
      }
      if (pendingScrollTimeoutRef.current !== null) {
        window.clearTimeout(pendingScrollTimeoutRef.current);
        pendingScrollTimeoutRef.current = null;
      }
      if (overflowCheckRafRef.current) {
        cancelAnimationFrame(overflowCheckRafRef.current);
        overflowCheckRafRef.current = 0;
      }
    };
  }, []);

  useEffect(() => {
    detectPinnedMessage();
  }, [messages, detectPinnedMessage]);

  // On remount with existing messages (scene transition), scroll to bottom
  // once the panel exits hidden mode.
  useEffect(() => {
    if (initialScrollDoneRef.current) return;
    if (messages.length === 0 || chatMode === "hidden") return;
    initialScrollDoneRef.current = true;
    requestAnimationFrame(() => {
      const history = historyRef.current;
      if (history) {
        history.scrollTop = history.scrollHeight;
        detectPinnedMessage();
      }
    });
  }, [messages, chatMode, detectPinnedMessage]);

  useEffect(() => {
    if (isInputFocused) {
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;

      if (isTouchDevice && chatMode !== "full-screen") {
        if (lastNonFullscreenModeRef.current === null) {
          lastNonFullscreenModeRef.current = chatMode;
        }
        if (requestedModeRef.current !== "full-screen") {
          setRequestedMode("full-screen");
        }
      }
      return;
    }

    if (isSliding || keyboardDismissGestureActiveRef.current) {
      return;
    }

    // Don't restore previous mode on blur — keep panel in full-screen.
    // User can swipe down to half-screen manually.
    if (lastNonFullscreenModeRef.current) {
      lastNonFullscreenModeRef.current = null;
      return;
    }

    if (chatMode !== "full-screen") {
      lastNonFullscreenModeRef.current = null;
    }
  }, [chatMode, isInputFocused, isSliding]);

  useEffect(() => {
    if (messages.length <= lastMessageCountRef.current) {
      lastMessageCountRef.current = messages.length;
      return;
    }

    lastMessageCountRef.current = messages.length;
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) {
      return;
    }
    if (lastMessage.role === "assistant" && chatMode === "hidden") {
      if (!isPoiArtifactOpen) {
        setRequestedMode("half-screen");
      }
      return;
    }
    if (lastMessage.role === "user" && chatMode === "hidden") {
      setRequestedMode("half-screen");
    }
  }, [chatMode, messages, isPoiArtifactOpen]);

  const latestAssistantMessage = useMemo(
    () => messages.findLast((message) => message.role === "assistant"),
    [messages]
  );
  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    if (!latestAssistantMessage) {
      setIsAnimatingMessage(false);
      lastAnimatedMessageIdRef.current = null;
      return;
    }

    const isNewMessage =
      latestAssistantMessage.id !== lastAnimatedMessageIdRef.current;
    if (isNewMessage) {
      lastAnimatedMessageIdRef.current = latestAssistantMessage.id;
    }
    setIsAnimatingMessage(!latestAssistantMessage.disableAnimation);
  }, [latestAssistantMessage]);

  const isGenerating = status !== "idle" && status !== "error";
  const effectiveStatus = isAnimatingMessage ? "idle" : status;
  const effectivePinned = chatMode === "hidden" ? null : pinnedMessage;
  const pinnedBarHeight = effectivePinned ? PINNED_BAR_HEIGHT : 0;

  const pinnedHeaderNode = (
    <PinnedUserMessage
      text={effectivePinned?.text ?? ""}
      visible={effectivePinned !== null}
      onClick={handlePinnedClick}
    />
  );

  const afterFooterNode =
    totalPoiCount > 0 ? (
      <PoiChips
        unvisitedSights={poiLocations.unvisited}
        visitedSights={poiLocations.visited}
        activeSightId={currentSight?.id ?? null}
        onSelect={handlePoiSelect}
        nextScene={nextSceneSnapshot}
        onNextSceneSelect={handleNextSceneChipSelect}
      />
    ) : undefined;

  const footerNode = (
    <ChatInput
      status={effectiveStatus}
      onSubmit={handleChatSubmit}
      onStop={handleStop}
      isGenerating={isGenerating}
      onFocus={handleInputFocus}
      onBlur={handleInputBlur}
      placeholder={i18n.assistant.placeholder}
      // Block input while a scene_open is in flight — without intro
      // context the chat is meaningless. Defence-in-depth alongside
      // the Effector-level filter in `assistantPromptSubmitted`.
      disabled={waitingSceneOpen}
    />
  );

  // History + thinking-status row + ScrollToBottom — identical inside
  // both wrappers. Both `AssistantChatPanel` (mobile) and
  // `AssistantChatSidebar` (desktop) render this fragment inside their
  // `.wrapper` (positioned absolutely so `.history` `inset: 8px 0 10px`
  // resolves correctly).
  const chatBody = (
    <>
      <div
        ref={historyRef}
        className={styles.history}
        onScroll={handleHistoryScroll}
      >
        <div ref={historyInRef} className={styles.historyIn}>
          {messages.map((message) => (
            <article
              key={message.id}
              data-role={message.role}
              data-message-id={message.id}
              className={cx(
                styles.message,
                message.role === "assistant"
                  ? styles.fromAssistant
                  : styles.fromUser
              )}
              aria-live={message.role === "assistant" ? "polite" : undefined}
            >
              <div className={styles.messageIn}>
                {message.role === "assistant" ? (
                  <>
                    <AssistantMessage
                      blocks={message.blocks}
                      links={message.links}
                      disableAnimation={
                        message.disableAnimation ||
                        !(
                          latestAssistantMessage?.id != null &&
                          lastMessage?.id === latestAssistantMessage.id &&
                          message.id === latestAssistantMessage.id
                        )
                      }
                      onComplete={() => handleTextAnimationComplete(message.id)}
                      onUpdate={
                        message.id === latestAssistantMessage?.id
                          ? checkContentOverflow
                          : undefined
                      }
                      onPhotosClick={handlePhotosClick}
                      onLinkClick={selectSuggestion}
                      onEnterScene={handleBannerEnterScene}
                    />
                    {!(message.id === latestAssistantMessage?.id && isAnimatingMessage) && (
                      <Suggestions
                        sights={sights}
                        suggestions={[...mockPoiArtifactSuggestions, ...message.suggestions].filter((s) => {
                          if (s.type === 'enter-poi-artifact' && s.sceneId === activeSceneId) return false;
                          if (s.type !== 'enter-poi-artifact') {
                            const suggestionSceneId = (s as { sceneId?: unknown }).sceneId;
                            if (
                              typeof suggestionSceneId === 'string' &&
                              suggestionSceneId !== activeScene?.poiFilterId
                            ) {
                              return false;
                            }
                          }
                          return true;
                        })}
                        handleSuggestion={selectSuggestion}
                      />
                    )}
                  </>
                ) : (
                  message.text
                )}
              </div>
            </article>
          ))}
          {(effectiveStatus === "sending" ||
            effectiveStatus === "awaiting") && (
            <article
              className={cx(styles.message, styles.fromAssistant)}
              aria-live="polite"
            >
              <div className={styles.messageIn}>
                <LoadingText>
                  {effectiveStatus === "sending"
                    ? i18n.assistant.status.sending
                    : thinkingText ?? i18n.assistant.status.thinking}
                </LoadingText>
              </div>
            </article>
          )}
        </div>
      </div>

      <ScrollToBottomButton
        visible={showScrollToBottom && chatMode !== "hidden"}
        onClick={scrollToBottom}
      />
    </>
  );

  return (
    <>
      {layoutMode === "desktop" ? (
        <AssistantChatSidebar
          animateInitialOffset={animateInitialOffset}
          hidden={hidden}
          pinnedHeader={pinnedHeaderNode}
          pinnedBarHeight={pinnedBarHeight}
          afterFooter={afterFooterNode}
          afterFooterHeight={poiChipsHeight}
          footer={footerNode}
        >
          {chatBody}
        </AssistantChatSidebar>
      ) : (
        <AssistantChatPanel
          onModeChange={handleModeChange}
          onGestureChange={handlePanelGestureChange}
          onKeyboardDismissGestureStart={handleKeyboardDismissGestureStart}
          requestedMode={requestedMode}
          dragRef={dragRef}
          animateInitialOffset={animateInitialOffset}
          hidden={hidden}
          pinnedHeader={pinnedHeaderNode}
          pinnedBarHeight={pinnedBarHeight}
          afterFooter={afterFooterNode}
          afterFooterHeight={poiChipsHeight}
          footer={footerNode}
        >
          {chatBody}
        </AssistantChatPanel>
      )}
      <PhotoViewer
        items={viewerItems}
        open={viewerOpen}
        index={viewerIndex}
        onClose={closeViewer}
        onIndexChange={setViewerIndex}
      />
    </>
  );
}
